import type {
    QueryKey,
    UseMutationOptions,
    UseQueryOptions,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import { fetcher } from './Fetcher';

const createCustomFetcher = async <
    ResponseData = unknown,
    Body = unknown,
    URLParams = unknown,
>(
    body: Body,
    endpoint: string | ((urlParams: URLParams) => string),
    method: Required<Parameters<typeof fetcher<ResponseData>>[0]>['method'],
    urlParams?: URLParams,
) => {
    const normalizedEndpoint =
        typeof endpoint === 'function'
            ? endpoint(urlParams as URLParams)
            : endpoint;

    const normalizedBody = body == null ? undefined : JSON.stringify(body);

    return await fetcher<ResponseData>({
        body: normalizedBody,
        endpoint: normalizedEndpoint,
        method,
    });
};

interface QueryBuilderArgs<QV = unknown> {
    endpoint: string | ((variables: QV) => string);
    /**
     * @description
     * if your query needs a computed key, you can pass a function that returns a `string`
     *
     * @example
     * (variables) => `user/${variables.id}`
     */
    queryKey:
        | string
        | ((variables: QV) => string)
        | ((variables: QV) => QueryKey);
    /**
     * Runs after a successful response. Throw to fail the query so React Query
     * retries it. Used when an empty body is valid for other endpoints but not
     * for this one.
     */
    assertResponse?: (data: unknown) => void;
}

/**
 *
 */
const queryBuilder =
    <
        QueryResponse = unknown,
        QueryVariables = unknown,
        QueryError = Error,
        QueryResult = QueryResponse,
    >(
        args: QueryBuilderArgs<QueryVariables>,
    ) =>
    (
        variables: QueryVariables,
        options?: Omit<
            UseQueryOptions<QueryResponse, QueryError, QueryResult>,
            'queryKey'
        >,
    ) => {
        const { endpoint, queryKey, assertResponse } = args;

        const baseQueryKey =
            typeof queryKey === 'function' ? queryKey(variables) : queryKey;
        const normalizedQueryKey: QueryKey = Array.isArray(baseQueryKey)
            ? baseQueryKey
            : [baseQueryKey];

        return useQuery<QueryResponse, QueryError, QueryResult>({
            queryKey: normalizedQueryKey,
            queryFn: async () => {
                const data = await createCustomFetcher<
                    QueryResponse,
                    null,
                    QueryVariables
                >(null, endpoint, 'GET', variables);
                assertResponse?.(data);
                return data;
            },
            ...options,
        });
    };

interface MutationBuilderArgs<MR = unknown, MUP = unknown> {
    endpoint: string | ((variables: MUP) => string);
    method: Exclude<
        Required<Parameters<typeof fetcher<MR>>[0]>['method'],
        'GET'
    >;
}

const mutationBuilder =
    <
        MutationResponse = unknown,
        MutationBody = unknown,
        MutationURLParams = unknown,
        MutationError = Error,
    >(
        args: MutationBuilderArgs<MutationResponse, MutationURLParams>,
    ) =>
    (
        options?: UseMutationOptions<
            MutationResponse,
            MutationError,
            { body?: MutationBody; urlParams?: MutationURLParams }
        >,
    ) => {
        const { endpoint, method } = args;

        return useMutation({
            mutationFn: async (variables) => {
                const { body, urlParams } = variables;
                return await createCustomFetcher(
                    body,
                    endpoint,
                    method,
                    urlParams,
                );
            },
            ...options,
        });
    };

export const requestBuilder = {
    mutation: mutationBuilder,
    query: queryBuilder,
};

export function buildQueryParams<
    QueryParams = Record<string, bigint | boolean | number | string | symbol>,
>(query: QueryParams): string {
    const searchParams = new URLSearchParams();

    for (const [paramName, value] of Object.entries(
        query as Record<string, unknown>,
    )) {
        searchParams.append(paramName, value?.toString() ?? '');
    }

    return `?${searchParams.toString()}`;
}
